import { useSelector } from "react-redux";
import { AccessDecision } from "../AccessGate.types";

interface RoleObject {
    type: string;
}

interface RootState {
    userState: {
        role: RoleObject | RoleObject[];
    };
}

/**
 * Strategy hook that checks whether the current user holds
 * at least one of the given roles.
 *
 * Mirrors the logic that lived inside `<Can>`, but returns
 * a generic `AccessDecision` consumable by `<AccessGate>`.
 *
 * @example
 * const access = useRoleStrategy([IRoles.Admin, IRoles.Business]);
 * <AccessGate strategy={access} behavior={HideBehavior}>…</AccessGate>
 */
export function useRoleStrategy(allowedRoles: string[]): AccessDecision {
    const roleData = useSelector((state: RootState) => state.userState.role);

    const roles: string[] = Array.isArray(roleData)
        ? roleData.map((r) => r.type)
        : [roleData.type];

    const granted = roles.some((userRole) => allowedRoles.includes(userRole));

    return {
        granted,
        reason: granted ? undefined : "NO_ROLE",
        meta: { userRoles: roles, allowedRoles },
    };
}
